import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { cssVariables } from "@/utils/utils";
import { Tabs } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import AntIcon from "../../../common/components/icons/AntIcon";
import { QueryKeys } from "../../../common/constants";
import { TabKeyRequest } from "../../../common/types";
import ProductGroupForm from "../components/forms/ProductGroupForm";
import { ProductModalForm } from "../components/forms/ProductModalForm";
import { ProductFilterView } from "../components/views/ProductFilterView";
import { ProductGroupFilterView } from "../components/views/ProductGroupFilterView";
import { ProductGroupTableView } from "../components/views/ProductGroupTableView";
import { ProductTableView } from "../components/views/ProductTableView";
import { Product, ProductGroup } from "../types";
import { PRODUCT_PAGE_TABS } from "../utils";

export const ProductsContainer = () => {
  const { t } = useTranslation();

  const [productGroupFormOpen, setProductGroupFormOpen] = useState<boolean>(false);
  const [productGroupToUpdate, setProductGroupToUpdate] = useState<ProductGroup>();

  const [productFormOpen, setProductFormOpen] = useState<boolean>(false);
  const [productToUpdate, setProductToUpdate] = useState<Product>();

  const navigate = useNavigate();
  const searchTabParam = useFilterSearchParams<TabKeyRequest>({ params: [QueryKeys.TAB] }).tab;

  const activeTab =
    searchTabParam && [PRODUCT_PAGE_TABS.PRODUCT_GROUPS, PRODUCT_PAGE_TABS.PRODUCTS].includes(searchTabParam)
      ? searchTabParam
      : PRODUCT_PAGE_TABS.PRODUCTS;

  const handleTabKeyChange = (tab: string) => {
    navigate({ search: URLQuery.searchParamsToString({ searchParams: { [QueryKeys.TAB]: tab } }) }, { replace: true });
  };

  useEffect(() => {
    if (!searchTabParam || activeTab !== searchTabParam) {
      navigate(
        { search: URLQuery.searchParamsToString({ searchParams: { [QueryKeys.TAB]: PRODUCT_PAGE_TABS.PRODUCTS } }) },
        { replace: true },
      );
    }
  }, [searchTabParam]);

  const handleProductGroupCreateClick = () => {
    setProductGroupToUpdate(undefined);
    setProductGroupFormOpen(true);
  };

  const handleProductGroupUpdateClick = (group: ProductGroup) => {
    setProductGroupToUpdate(group);
    setProductGroupFormOpen(true);
  };

  const handleProductGroupFormCancel = () => {
    setProductGroupFormOpen(false);
    setProductGroupToUpdate(undefined);
  };

  const handleProductCreateClick = () => {
    setProductToUpdate(undefined);
    setProductFormOpen(true);
  };

  const handleProductUpdateClick = (product: Product) => {
    setProductToUpdate(product);
    setProductFormOpen(true);
  };

  const handleProductFormCancel = () => {
    setProductFormOpen(false);
    setProductToUpdate(undefined);
  };

  return (
    <ContentWrapper>
      <Tabs
        tabBarStyle={{
          backgroundColor: "white",
          padding: "0 24px",
          marginBottom: 24,
          borderRadius: 8,
          boxShadow: `0 1px 3px 0 ${cssVariables.colorShadow}, 0 1px 2px 0 ${cssVariables.colorShadow}`,
          border: `1px solid ${cssVariables.colorHover}`,
          overflow: "hidden",
        }}
        activeKey={activeTab}
        onChange={handleTabKeyChange}
        items={[
          {
            key: PRODUCT_PAGE_TABS.PRODUCT_GROUPS,
            icon: <AntIcon type="group" />,
            label: t("product.titles.productGroups"),
            children: (
              <div>
                <ProductGroupFilterView onCreateClick={handleProductGroupCreateClick} />

                <ProductGroupTableView onUpdateClick={handleProductGroupUpdateClick} />

                <ProductGroupForm
                  open={productGroupFormOpen}
                  onFormCancel={handleProductGroupFormCancel}
                  productGroup={productGroupToUpdate}
                />
              </div>
            ),
          },
          {
            key: PRODUCT_PAGE_TABS.PRODUCTS,
            icon: <AntIcon type="unordered-list" />,
            label: t("product.titles.products"),
            children: (
              <div>
                <ProductFilterView onCreateClick={handleProductCreateClick} />

                <ProductTableView onUpdateClick={handleProductUpdateClick} />

                <ProductModalForm
                  open={productFormOpen}
                  onFormCancel={handleProductFormCancel}
                  product={productToUpdate}
                />
              </div>
            ),
          },
        ]}
      />
    </ContentWrapper>
  );
};
